import { useState, useEffect } from 'react';
import { 
  Building2, Save, Trash2, Image as ImageIcon, CheckCircle2, 
  AlertTriangle, Globe, Lock, ChevronRight, Layers, Shield, Zap,
  Cpu, MessageSquare, Database, FileCode
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace, useUpdateWorkspace, useDeleteWorkspace } from '@/api/queries';
import LoadingState from '../ui/LoadingState';
import ErrorState from '../ui/ErrorState';

interface WorkspaceSettingsTabProps {
  workspaceId?: string;
}

export default function WorkspaceSettingsTab({ workspaceId }: WorkspaceSettingsTabProps) {
  const { organization } = useAuth();
  const organizationId = organization?.id;
  const [settingsTab, setSettingsTab] = useState<'identidade' | 'plano' | 'seguranca' | 'integracoes'>('identidade');
  const [saving, setSaving] = useState(false);

  const { data: workspace, isLoading, error, refetch } = useWorkspace(workspaceId || '', organizationId || '');
  const updateWorkspaceMutation = useUpdateWorkspace();
  const deleteWorkspaceMutation = useDeleteWorkspace();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: 'workspace-slug',
    isPublic: false
  });

  const [integrations, setIntegrations] = useState({
    slack: true,
    github: false,
    googleDrive: true,
    discord: false
  });

  const [securityPolicies, setSecurityPolicies] = useState({
    ssoRequired: true,
    mfaRequired: false,
    ipWhitelist: '189.122.40.*, 200.141.100.*'
  });

  useEffect(() => {
    if (workspace) {
      const ws = Array.isArray(workspace) ? workspace[0] : workspace;
      setFormData({
        name: ws.name || '',
        description: ws.description || '',
        slug: (ws.name || 'workspace').toLowerCase().replace(/\s+/g, '-'),
        isPublic: false
      });
    }
  }, [workspace]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !workspaceId) return;
    try {
      setSaving(true);
      await updateWorkspaceMutation.mutateAsync({
        id: workspaceId,
        name: formData.name,
        description: formData.description,
        settings: {
          ...(typeof workspace === 'object' && !Array.isArray(workspace) ? workspace.settings : {}),
          description: formData.description,
        },
      });
      await refetch();
      alert('Configurações do Workspace atualizadas com sucesso!');
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;
    const confirmDelete = confirm(
      `ATENÇÃO: Tem certeza que deseja EXCLUIR permanentemente o workspace "${formData.name}"?\nEsta ação é irreversível e apagará todos os dados associados.`
    );
    if (!confirmDelete) return;
    try {
      await deleteWorkspaceMutation.mutateAsync(workspaceId);
      alert('Workspace excluído com sucesso!');
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir o workspace.');
    }
  };

  if (!workspaceId) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[#64748B] font-medium">
        Selecione um workspace para gerenciar as configurações.
      </div>
    );
  }

  if (isLoading) return <LoadingState message="Carregando configurações do workspace..." />;
  if (error) return <ErrorState message={error.message || 'Erro ao carregar workspace'} retry={() => refetch()} />;

  return (
    <div className="flex flex-col gap-8 h-full max-w-4xl animate-fade-in" id="workspace-settings-container">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-bold text-[#111111] tracking-tight">Administração do Workspace</h3>
        <p className="text-sm text-[#64748B] font-medium">Gerencie a identidade, faturamento, segurança e integrações da sua organização</p>
      </div>

      <div className="flex gap-1 bg-[#FAFAFB] p-1 rounded-2xl border border-slate-100 max-w-fit shadow-xs">
        {[
          { id: 'identidade', label: 'Identidade Geral' },
          { id: 'plano', label: 'Plano & Limites' },
          { id: 'seguranca', label: 'Segurança & SSO' },
          { id: 'integracoes', label: 'Integrações' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSettingsTab(tab.id as any)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              settingsTab === tab.id 
                ? 'bg-white text-neutral-900 shadow-xs border border-slate-200/50' 
                : 'text-slate-500 hover:text-slate-900 hover:bg-neutral-50/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-8">
        {settingsTab === 'identidade' && (
          <div className="flex flex-col gap-8 animate-fade-in">
            <div className="bg-white border border-[#0F172A0A] rounded-[40px] p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-neutral-50 flex items-center justify-center text-[#111111]">
                  <Building2 size={20} />
                </div>
                <h4 className="text-lg font-bold text-[#111111] tracking-tight">Identidade Visual</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-32 h-32 rounded-[32px] bg-[#F8FAFC] border-2 border-dashed border-[#0F172A1A] flex flex-col items-center justify-center text-[#94A3B8] group cursor-pointer hover:border-black/20 transition-all overflow-hidden relative shadow-inner">
                    <ImageIcon size={32} />
                    <span className="text-[10px] font-black uppercase tracking-widest mt-2">Alterar Logo</span>
                    {formData.name && (
                       <div className="absolute inset-0 bg-black flex items-center justify-center text-white text-3xl font-display font-bold opacity-0 group-hover:opacity-10 transition-opacity">
                         {formData.name.charAt(0)}
                       </div>
                    )}
                  </div>
                  <p className="text-[10px] text-[#64748B] font-bold text-center uppercase tracking-widest leading-relaxed">
                    Recomendado: 512x512px<br/>PNG ou SVG
                  </p>
                </div>

                <div className="md:col-span-2 flex flex-col gap-6">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest px-1">Nome do Workspace</label>
                    <input 
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-[#FAFAFB] border border-[#0F172A0A] rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none transition-all"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest px-1">Descrição Curta</label>
                    <textarea 
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full bg-[#FAFAFB] border border-[#0F172A0A] rounded-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none transition-all resize-none"
                      placeholder="Descreva o propósito deste workspace..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#0F172A0A] rounded-[40px] p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-neutral-50 flex items-center justify-center text-[#111111]">
                  <Globe size={20} />
                </div>
                <h4 className="text-lg font-bold text-[#111111] tracking-tight">Visibilidade & URL</h4>
              </div>

              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-[#64748B] uppercase tracking-widest px-1">Identificador Único (Slug)</label>
                  <div className="flex items-center">
                    <div className="bg-[#F1F5F9] border border-[#0F172A0A] border-r-0 rounded-l-2xl p-4 text-xs font-bold text-[#64748B] select-none">
                      cyzor.io/control/
                    </div>
                    <input 
                      type="text" 
                      value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value})}
                      className="flex-1 bg-[#FAFAFB] border border-[#0F172A0A] rounded-r-2xl p-4 text-sm font-bold focus:ring-2 focus:ring-black/5 outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-6 bg-[#FAFAFB] rounded-3xl border border-[#0F172A05]">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white border border-[#0F172A0A] flex items-center justify-center text-[#111111] shadow-xs">
                      <Lock size={18} />
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-[#111111]">Visibilidade</h5>
                      <p className="text-xs text-[#64748B] font-medium">Este workspace é privado</p>
                    </div>
                  </div>
                  <div className="bg-black text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider">Privado</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-rose-100 rounded-[40px] p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600 border border-rose-100">
                  <AlertTriangle size={20} />
                </div>
                <h4 className="text-lg font-bold text-rose-900 tracking-tight">Zona de Perigo</h4>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 bg-rose-50/50 rounded-3xl border border-rose-100/50">
                <div>
                  <h5 className="text-sm font-bold text-rose-950 tracking-tight">Excluir este Workspace</h5>
                  <p className="text-xs text-rose-800/70 font-medium leading-relaxed">
                    Isso removerá permanentemente todos os projetos, produtos, faturas e dados associados a este workspace. Esta ação não pode ser desfeita.
                  </p>
                </div>
                <button 
                  type="button" 
                  onClick={handleDeleteWorkspace}
                  className="bg-rose-600 text-white px-6 py-3 rounded-2xl text-xs font-bold hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/10 whitespace-nowrap cursor-pointer"
                >
                  Excluir Workspace
                </button>
              </div>
            </div>
          </div>
        )}

        {settingsTab === 'identidade' && (
          <div className="flex justify-end gap-4 mt-4">
            <button type="button" className="px-8 py-4 rounded-2xl text-sm font-bold text-[#64748B] hover:text-[#111111] transition-all cursor-pointer">
              Descartar Alterações
            </button>
            <button 
              type="submit" 
              disabled={saving}
              className="bg-black text-white px-10 py-4 rounded-2xl text-sm font-bold hover:bg-neutral-800 transition-all shadow-xl shadow-black/10 flex items-center gap-2 cursor-pointer"
            >
              {saving ? 'Salvando...' : 'Salvar Alterações'}
              {!saving && <Save size={18} />}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
